import { LogManager } from '../utils/LogManager';
import { getDashboardIsMounted } from '../components/Dashboard';

const logManager = new LogManager('WebserviceAvailabilityChecker');

export enum WebserviceStatus {
    Available,
    Verifying,
    Unavailable
}

export class WebserviceAvailabilityChecker {
    private intervalId: NodeJS.Timer | null = null;

    private readonly UNAVAILABILITY_VERIFY_PING_INTERVAL_MS: number = 2000;
    private readonly UNAVAILABILITY_VERIFY_PING_COUNT: number = 4;
    private readonly RESTORE_PING_INTERVAL_MS: number = 5000;

    private isWebserviceAvailable: boolean = true;
    private availabilityChecksCount: number = 0;
    private verificationEventSent: boolean = false;

    constructor(
        private possibleUnavailabilityEventName: string,
        private checkWebserviceIsAvailable: () => Promise<boolean>,
        private onStatusChangeCallback: (currentStatus: WebserviceStatus) => void
    ) {
        this.pingWebservice = this.pingWebservice.bind(this);
        this.startWebservicePinging = this.startWebservicePinging.bind(this);
    }

    registerListeners(): void {
        document.addEventListener(this.possibleUnavailabilityEventName, this.startWebservicePinging);
    }

    unregisterListeners(): void {
        document.removeEventListener(this.possibleUnavailabilityEventName, this.startWebservicePinging);
    }

    private async pingWebservice(): Promise<void> {
        logManager.log('Pinging webservice...');

        const isWebserviceAvailable = await this.checkWebserviceIsAvailable();
        if (isWebserviceAvailable) {
            this.restoreAvailableState();
            return;
        }

        if (!this.isWebserviceAvailable) { return; }

        this.availabilityChecksCount++;
        if (this.availabilityChecksCount >= this.UNAVAILABILITY_VERIFY_PING_COUNT) {
            logManager.log('Verifying webservice is unavailable.');
            this.isWebserviceAvailable = false;
            this.restartWebservicePinging();
            this.onStatusChangeCallback(WebserviceStatus.Unavailable);
        }
    }

    private restoreAvailableState(): void {
        logManager.log('Restoring webservice availability.');
        this.verificationEventSent = false;
        this.isWebserviceAvailable = true;
        this.availabilityChecksCount = 0;
        this.stopWebservicePinging();
        this.onStatusChangeCallback(WebserviceStatus.Available);
    }

    private restartWebservicePinging(): void {
        this.stopWebservicePinging();
        this.startWebservicePinging();
    }

    private stopWebservicePinging(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    private startWebservicePinging(event?: any): void {
        if (!this.verificationEventSent) {
            const eventIsRelatedToDomainRequests =
                event &&
                event.detail &&
                (
                    event.detail.indexOf('domains') ||
                    event.detail.indexOf('filespaces')
                );
            if (!getDashboardIsMounted() || !eventIsRelatedToDomainRequests) {
                this.onStatusChangeCallback(WebserviceStatus.Verifying);
                this.verificationEventSent = true;
            }
        }

        if (this.intervalId) { return; }

        const interval = this.isWebserviceAvailable ?
            this.UNAVAILABILITY_VERIFY_PING_INTERVAL_MS :
            this.RESTORE_PING_INTERVAL_MS;
        logManager.log(`Starting pinging in speed appropriate for ${this.isWebserviceAvailable ? 'verifying unavailability' : 'restoration'}`);
        this.intervalId = setInterval(
            this.pingWebservice,
            interval
        );
    }
}



// WEBPACK FOOTER //
// ./src/webportal/services/WebserviceAvailabilityChecker.ts